export interface Vec2 {
  readonly x: number;
  readonly y: number;
}

export const vec = (x: number, y: number): Vec2 => ({ x, y });

export const add = (left: Vec2, right: Vec2): Vec2 =>
  vec(left.x + right.x, left.y + right.y);

export const subtract = (left: Vec2, right: Vec2): Vec2 =>
  vec(left.x - right.x, left.y - right.y);

export const scale = (value: Vec2, factor: number): Vec2 =>
  vec(value.x * factor, value.y * factor);

export const dot = (left: Vec2, right: Vec2): number =>
  left.x * right.x + left.y * right.y;

export const magnitudeSquared = (value: Vec2): number => dot(value, value);

export const magnitude = (value: Vec2): number =>
  Math.sqrt(magnitudeSquared(value));

export const distanceSquared = (left: Vec2, right: Vec2): number =>
  magnitudeSquared(subtract(left, right));

export const distance = (left: Vec2, right: Vec2): number =>
  Math.sqrt(distanceSquared(left, right));

export const lerp = (from: Vec2, to: Vec2, amount: number): Vec2 =>
  vec(from.x + (to.x - from.x) * amount, from.y + (to.y - from.y) * amount);

export const fromAngle = (angle: number): Vec2 =>
  vec(Math.cos(angle), Math.sin(angle));

export const angleOf = (value: Vec2): number => Math.atan2(value.y, value.x);

export const clamp = (
  value: number,
  minimum: number,
  maximum: number,
): number => Math.min(maximum, Math.max(minimum, value));

export const normalizeAngle = (angle: number): number => {
  if (!Number.isFinite(angle)) return 0;
  const fullTurn = Math.PI * 2;
  const normalized =
    ((((angle + Math.PI) % fullTurn) + fullTurn) % fullTurn) - Math.PI;
  return normalized === -Math.PI ? Math.PI : normalized;
};

export const angleDelta = (from: number, to: number): number =>
  normalizeAngle(to - from);

export const rotateTowards = (
  from: number,
  to: number,
  maximumDelta: number,
): number => {
  const delta = angleDelta(from, to);
  if (Math.abs(delta) <= maximumDelta) return normalizeAngle(to);
  return normalizeAngle(from + Math.sign(delta) * Math.max(0, maximumDelta));
};

export const finiteVec = (value: Vec2): boolean =>
  Number.isFinite(value.x) && Number.isFinite(value.y);
